import { useState } from "react";
import { useAuthStore } from "../../stores/authStore";
import { SsoConfigCard } from "../../components/shared/SsoConfigCard";
import { PageHeader } from "../../components/shared/PageHeader";
import { AlertTriangle, Plus, X } from "lucide-react";
import { rootDomain } from "../../lib/subdomain";
import { useEnterpriseSettings, useUpdateEnterpriseSettings } from "@platform/hooks";
import { useToast } from "@nthamizh/ui";

interface ToggleRowProps { label: string; description: string; checked: boolean; onChange: (v: boolean) => void }

function ToggleRow({ label, description, checked, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between py-3.5 border-b border-ds-border last:border-0">
      <div>
        <p className="text-sm font-medium text-ds-text">{label}</p>
        <p className="text-xs text-ds-text-muted mt-0.5">{description}</p>
      </div>
      <button role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ml-4 ${checked ? "bg-indigo-600" : "bg-gray-200"}`}>
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

export function EnterpriseSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const isPlatformAdmin = user?.platformRole === "platform_admin";
  const { data: settings, isLoading } = useEnterpriseSettings();
  const updateSettings = useUpdateEnterpriseSettings();
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [domains, setDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState("");
  const [notifs, setNotifs] = useState({ userRequests: true, migration: true, roles: true, digest: false });
  const [confirmText, setConfirmText] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  // Sync from API once loaded
  const [synced, setSynced] = useState(false);
  if (settings && !synced) {
    setName(settings.name);
    setDomains((settings as unknown as { allowedDomains?: string[] }).allowedDomains ?? []);
    setSynced(true);
  }

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ name, allowedDomains: domains } as unknown as { name?: string });
      toast({ title: "Settings saved", variant: "success" });
    } catch {
      toast({ title: "Failed to save settings", variant: "error" });
    }
  };

  const addDomain = () => {
    const d = newDomain.trim().toLowerCase().replace(/^@/, "");
    if (!d || domains.includes(d)) return;
    setDomains((prev) => [...prev, d]);
    setNewDomain("");
  };

  const removeDomain = (d: string) => setDomains((prev) => prev.filter((x) => x !== d));

  if (isLoading) return <div className="p-8 text-center text-sm text-ds-text-muted">Loading settings...</div>;

  return (
    <div className="space-y-5 max-w-2xl">
      <PageHeader title="Enterprise settings" subtitle="Manage your enterprise configuration" />

      {/* Profile — platform admin only; enterprise admin cannot rename/subdomains */}
      {isPlatformAdmin && (
      <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
        <h2 className="text-sm font-semibold text-ds-text mb-4">Enterprise profile</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Enterprise name</label>
            <input value={name} onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-ds-border rounded-lg focus:outline-none focus:border-indigo-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-ds-text-secondary mb-1.5">Subdomain</label>
            <div className="flex">
              <input defaultValue={settings?.slug} disabled
                className="flex-1 px-3 py-2 text-sm border border-ds-border rounded-l-lg bg-ds-bg opacity-60" />
              <span className="px-3 py-2 text-sm text-ds-text-muted border border-l-0 border-ds-border rounded-r-lg bg-ds-bg">.{rootDomain()}</span>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={handleSave} disabled={updateSettings.isPending}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            {updateSettings.isPending ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>
      )}

      {/* Domain approval — platform admin only */}
      {isPlatformAdmin && (
      <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
        <h2 className="text-sm font-semibold text-ds-text mb-1">User registration — domain approval</h2>
        <p className="text-xs text-ds-text-muted mb-4">
          Users with email addresses matching these domains are automatically approved on registration.
          Leave empty to require manual approval for all users.
        </p>
        <div className="flex flex-wrap gap-2 mb-3">
          {domains.length === 0 && (
            <span className="text-xs text-ds-text-muted italic">No domains configured — all registrations require manual approval.</span>
          )}
          {domains.map((d) => (
            <span key={d} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-xs text-indigo-700 font-medium">
              @{d}
              <button onClick={() => removeDomain(d)} className="text-indigo-400 hover:text-indigo-700">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="flex items-center flex-1 border border-ds-border rounded-lg overflow-hidden">
            <span className="px-2.5 text-sm text-ds-text-muted">@</span>
            <input
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addDomain()}
              placeholder="acme.com"
              className="flex-1 py-2 pr-3 text-sm focus:outline-none bg-transparent"
            />
          </div>
          <button onClick={addDomain}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-sm font-medium">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={handleSave} disabled={updateSettings.isPending}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
            Save domain settings
          </button>
        </div>
      </div>
      )}

      {/* SSO */}
      <SsoConfigCard
        enterpriseId={settings?.id ?? ""}
        enterpriseSlug={window.location.hostname.split(".")[0] ?? ""}
      />

      {/* Notifications */}
      <div className="bg-white border border-ds-border rounded-card shadow-card p-5">
        <h2 className="text-sm font-semibold text-ds-text mb-1">Notifications</h2>
        <ToggleRow label="User registration requests" description="Email admin when a user requests access." checked={notifs.userRequests} onChange={(v) => setNotifs((n) => ({ ...n, userRequests: v }))} />
        <ToggleRow label="Migration activity" description="Notify on Fast Formula sync events." checked={notifs.migration} onChange={(v) => setNotifs((n) => ({ ...n, migration: v }))} />
        <ToggleRow label="Role changes" description="When roles or permissions are updated." checked={notifs.roles} onChange={(v) => setNotifs((n) => ({ ...n, roles: v }))} />
        <ToggleRow label="Weekly digest" description="Summary of enterprise activity each Monday." checked={notifs.digest} onChange={(v) => setNotifs((n) => ({ ...n, digest: v }))} />
      </div>

      {/* Danger zone */}
      {isPlatformAdmin ? (
        <div className="bg-white border border-red-200 rounded-card shadow-card p-5">
          <h2 className="text-sm font-semibold text-red-600 flex items-center gap-1.5 mb-4">
            <AlertTriangle className="w-4 h-4" /> Danger zone
          </h2>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-ds-text">Reset enterprise</p>
              <p className="text-xs text-ds-text-muted mt-0.5">Removes all users, roles, and configuration. Cannot be undone.</p>
            </div>
            <button onClick={() => setShowConfirm(true)}
              className="flex-shrink-0 px-3.5 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 text-sm font-medium">
              Reset enterprise
            </button>
          </div>
          {showConfirm && (
            <div className="mt-4 p-4 rounded-lg border border-red-200 bg-red-50 space-y-3">
              <p className="text-sm text-red-700">Type <strong>RESET</strong> to confirm. This action is permanent.</p>
              <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type RESET"
                className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg focus:outline-none focus:border-red-500" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                  className="px-3 py-1.5 rounded-lg bg-white border border-ds-border text-sm text-ds-text-secondary hover:bg-ds-bg">Cancel</button>
                <button disabled={confirmText !== "RESET"}
                  className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-red-700">
                  Confirm reset
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-ds-border rounded-card shadow-card p-5 flex items-start gap-3 opacity-75">
          <div className="w-9 h-9 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-ds-text">Reset enterprise — Platform Admin only</p>
            <p className="text-xs text-ds-text-muted mt-1">Contact your platform administrator to reset this enterprise.</p>
          </div>
        </div>
      )}
    </div>
  );
}
